/**
 * Pagina /superadmin/tenencias — Sprint 13 S13-13.
 *
 * Mantenedor del super_admin para vincular Personas a Lotes durante
 * la carga inicial del barrio (LIVE). Cumple doctrina Portal vs
 * Superadmin §4.2.
 *
 * El CIERRE de tenencia (flujo ex-vecino) NO esta aqui — vive en el
 * portal vecino bajo Vista Comision /admin/tenencias (S13-07). Esta
 * pagina solo CREA tenencias nuevas.
 *
 * UI: listado de tenencias activas + filtros (lote/persona/sector) +
 * boton "+ Nueva tenencia" (modal que consume POST /tenencias/ de
 * S13-09 con side-effect de rol vecino).
 *
 * RBAC: require_super_admin_o_lectura_barrio (GET) y
 * require_super_admin (POST). Esta UI vive en sa.calbucura.cl, asi
 * que solo la cuenta institucional accede via gate de dominio (S13-01).
 */
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { ApiError } from '../../lib/api/client';
import { formatRut } from '../../lib/format';
import { listLotes } from '../../lib/api/lotes';
import { listPersonas } from '../../lib/api/personas';
import {
  actualizarTenencia,
  crearTenencia,
  eliminarTenencia,
  importTenenciasCsv,
  listTenencias,
} from '../../lib/api/tenencias';
import { ImportCsvSection } from '../../components/ImportCsvSection';
import type { CsvColumna } from '../../components/ImportCsvSection';
import type {
  LoteResumen,
  PersonaConRoles,
  Sector,
  TenenciaConPersonaLote,
  TenenciaCreate,
  TipoTenenciaLiteral,
} from '../../lib/api/types';
import { ETIQUETA_TIPO_TENENCIA_SA } from '../../lib/api/types';
import { Button } from '../../components/ui/Button';
import { ActionButton } from '../../components/ui/ActionButton';
import {
  Table,
  TBody,
  THead,
  Th,
  Tr,
  Td,
} from '../../components/ui/DataTable';
import { PageHeader } from '../../components/layout/PageHeader';
import { InputFecha } from '../../components/ui/InputFecha';

interface ModalCrearProps {
  lotes: LoteResumen[];
  personas: PersonaConRoles[];
  onCerrar: () => void;
  onCreado: (motivoNoOtorgado: string | null) => void;
}

function ModalCrear({
  lotes,
  personas,
  onCerrar,
  onCreado,
}: ModalCrearProps) {
  const [loteId, setLoteId] = useState('');
  const [personaId, setPersonaId] = useState('');
  const [tipo, setTipo] = useState<TipoTenenciaLiteral>('propietario_residente');
  const [desde, setDesde] = useState('');
  const [responsablePago, setResponsablePago] = useState(true);
  const [accesoPortal, setAccesoPortal] = useState(true);
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const valido = loteId !== '' && personaId !== '' && desde !== '';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valido || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload: TenenciaCreate = {
        lote_id: loteId,
        persona_id: personaId,
        tipo,
        desde,
        responsable_pago_cuotas: responsablePago,
        acceso_portal_habilitado: accesoPortal,
        notas: notas.trim() || null,
      };
      const resp = await crearTenencia(payload);
      onCreado(resp.motivo_no_otorgado);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label="Nueva tenencia"
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-xl w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Nueva tenencia
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Casa</span>
            <select
              value={loteId}
              onChange={(e) => setLoteId(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              <option value="">— elige —</option>
              {lotes.map((l) => (
                <option
                  key={l.id}
                  value={l.id}
                  className="bg-bg-sunken text-text-primary"
                >
                  {l.identificador} ({l.sector})
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Persona</span>
            <select
              value={personaId}
              onChange={(e) => setPersonaId(e.target.value)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              <option value="">— elige —</option>
              {personas.map((p) => (
                <option
                  key={p.id}
                  value={p.id}
                  className="bg-bg-sunken text-text-primary"
                >
                  {p.nombre_completo} ({formatRut(p.rut)})
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Tipo</span>
            <select
              value={tipo}
              onChange={(e) => setTipo(e.target.value as TipoTenenciaLiteral)}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              required
            >
              {Object.entries(ETIQUETA_TIPO_TENENCIA_SA).map(([k, v]) => (
                <option
                  key={k}
                  value={k}
                  className="bg-bg-sunken text-text-primary"
                >
                  {v}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm space-y-1">
            <span className="text-text-secondary">Desde</span>
            <InputFecha
              value={desde}
              onChange={setDesde}
              className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
              requerido
            />
          </label>
        </div>

        <div className="flex items-center gap-4 text-sm">
          <label className="flex items-center gap-2 text-text-secondary">
            <input
              type="checkbox"
              checked={responsablePago}
              onChange={(e) => setResponsablePago(e.target.checked)}
            />
            <span>Responsable de pago de cuotas</span>
          </label>
          <label className="flex items-center gap-2 text-text-secondary">
            <input
              type="checkbox"
              checked={accesoPortal}
              onChange={(e) => setAccesoPortal(e.target.checked)}
            />
            <span>Acceso al portal</span>
          </label>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <div className="bg-bg-elevated border-l-4 border-admin-red rounded-sm p-3 text-xs text-text-secondary">
          Si la persona ya tiene cuenta operativa, se le otorga el rol{' '}
          <span className="font-medium text-text-primary">vecino</span>{' '}
          automáticamente. Si no, el rol se asigna cuando se cree la cuenta.
        </div>

        {error && (
          <div
            className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={!valido || submitting}
          >
            {submitting ? 'Creando…' : 'Crear tenencia'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const TENENCIAS_CSV_COLUMNAS: CsvColumna[] = [
  {
    nombre: 'casa_identificador',
    obligatoria: true,
    ejemplo: '6931',
    notas: 'Debe existir previamente en Casas.',
  },
  {
    nombre: 'persona_rut',
    obligatoria: false,
    ejemplo: '12345678-9',
    notas: 'Si la persona tiene RUT en DB, este matcha. Si no, usar persona_nombre.',
  },
  {
    nombre: 'persona_nombre',
    obligatoria: false,
    ejemplo: 'Juan Pérez González',
    notas: 'Match case-insensitive. Solo si persona_rut está vacío.',
  },
  {
    nombre: 'tipo',
    obligatoria: true,
    ejemplo: 'propietario_residente',
    notas: 'Valores: propietario_residente, propietario_no_residente, arrendatario, comodatario, otro.',
  },
  {
    nombre: 'desde',
    obligatoria: true,
    ejemplo: '2026-05-14',
    notas: 'Formato YYYY-MM-DD.',
  },
  {
    nombre: 'responsable_pago_cuotas',
    obligatoria: false,
    ejemplo: 'si',
    notas: "'si' o 'no'. Default 'si'.",
  },
  {
    nombre: 'acceso_portal_habilitado',
    obligatoria: false,
    ejemplo: 'si',
    notas: "'si' o 'no'. Default 'si'.",
  },
  {
    nombre: 'notas',
    obligatoria: false,
    ejemplo: '',
    notas: 'Texto libre opcional.',
  },
];

// Sprint 13 S13-19 / S13-21: modal de edicion de tenencia.
// Permite cambiar la casa (lote_id) — caso "le asocie a la casa equivocada".
// Si la tenencia tiene cuotas, el backend las migra al nuevo lote.
interface ModalEditarTenenciaProps {
  tenencia: TenenciaConPersonaLote;
  lotes: LoteResumen[];
  onCerrar: () => void;
  onActualizada: () => void;
}

function ModalEditarTenencia({
  tenencia,
  lotes,
  onCerrar,
  onActualizada,
}: ModalEditarTenenciaProps) {
  const [loteId, setLoteId] = useState(tenencia.lote_id);
  const [tipo, setTipo] = useState<TipoTenenciaLiteral>(
    tenencia.tipo as TipoTenenciaLiteral,
  );
  const [desde, setDesde] = useState(tenencia.desde);
  const [respPago, setRespPago] = useState(tenencia.responsable_pago_cuotas);
  const [notifMorosidad, setNotifMorosidad] = useState(
    tenencia.notificar_morosidad,
  );
  const [notas, setNotas] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const casaCambio = loteId !== tenencia.lote_id;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (casaCambio) {
      const lote = lotes.find((l) => l.id === loteId);
      const ok = window.confirm(
        `Estás moviendo la tenencia a la casa ${lote?.identificador ?? loteId}. ` +
          'Si la tenencia tiene cuotas, también se moverán a la nueva casa. ' +
          '¿Confirmar?',
      );
      if (!ok) return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await actualizarTenencia(tenencia.id, {
        lote_id: loteId,
        tipo,
        desde,
        responsable_pago_cuotas: respPago,
        notificar_morosidad: notifMorosidad,
        notas: notas.trim() || null,
      });
      onActualizada();
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setError(detail);
      } else {
        setError('Error de red al actualizar');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50"
      role="dialog"
      aria-modal="true"
      aria-label={`Editar tenencia casa ${tenencia.lote_identificador}`}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border-[0.5px] border-white/15 rounded-md p-6 max-w-lg w-full space-y-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-medium text-text-primary">
            Editar tenencia
          </h2>
          <button
            type="button"
            onClick={onCerrar}
            className="text-text-muted hover:text-text-primary text-xl leading-none"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <div className="text-sm text-text-secondary">
          Persona <span className="font-medium text-text-primary">{tenencia.persona_nombre_completo}</span>
        </div>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Casa</span>
          <select
            value={loteId}
            onChange={(e) => setLoteId(e.target.value)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            {lotes.map((l) => (
              <option key={l.id} value={l.id} className="bg-bg-sunken text-text-primary">
                {l.identificador} ({l.sector})
              </option>
            ))}
          </select>
          {casaCambio && (
            <span className="text-xs text-state-warning block mt-1">
              ⚠ Mover la casa también migra las cuotas asociadas al nuevo lote.
            </span>
          )}
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Tipo</span>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value as TipoTenenciaLiteral)}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          >
            {Object.entries(ETIQUETA_TIPO_TENENCIA_SA).map(([k, v]) => (
              <option key={k} value={k} className="bg-bg-sunken text-text-primary">
                {v}
              </option>
            ))}
          </select>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Desde</span>
          <InputFecha
            value={desde}
            onChange={setDesde}
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        <label className="flex items-start gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={respPago}
            onChange={(e) => setRespPago(e.target.checked)}
            className="mt-1"
          />
          <span>
            Responsable de pago de cuotas
            <span className="block text-xs text-text-muted">
              Solo una tenencia activa por casa puede serlo. Al marcar
              esta, se libera automáticamente a la responsable anterior
              (ej: la casa pasa a arrendada y el arrendatario asume el
              pago).
            </span>
          </span>
        </label>

        <label className="flex items-start gap-2 text-sm text-text-secondary">
          <input
            type="checkbox"
            checked={notifMorosidad}
            onChange={(e) => setNotifMorosidad(e.target.checked)}
            className="mt-1"
          />
          <span>
            Notificar morosidad
            <span className="block text-xs text-text-muted">
              Si lo desmarcas, esta casa acumula deuda igual (sigue en los
              reportes) pero NO recibe los avisos de morosidad. Para vecinos que
              no se sumaron al proyecto.
            </span>
          </span>
        </label>

        <label className="text-sm space-y-1 block">
          <span className="text-text-secondary">Notas (opcional)</span>
          <textarea
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            rows={2}
            placeholder="Vacío = sin notas."
            className="w-full bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          />
        </label>

        {error && (
          <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary" role="alert">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" type="button" onClick={onCerrar}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" disabled={submitting}>
            {submitting ? 'Guardando…' : 'Guardar cambios'}
          </Button>
        </div>
      </form>
    </div>
  );
}

const ETIQUETA_MOTIVO: Record<string, string> = {
  ya_vigente: 'la persona ya tenía rol vecino vigente',
  persona_sin_usuario_operativo:
    'la persona aún no tiene cuenta operativa — el rol se asignará al crearla',
  persona_con_multiples_usuarios:
    'la persona tiene múltiples cuentas operativas — revisar manualmente',
  acceso_portal_deshabilitado:
    'la tenencia se creó sin acceso al portal — el rol vecino no aplica',
};

export function TenenciasSaPage() {
  const [tenencias, setTenencias] = useState<TenenciaConPersonaLote[]>([]);
  const [lotes, setLotes] = useState<LoteResumen[]>([]);
  const [personas, setPersonas] = useState<PersonaConRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filtroSector, setFiltroSector] = useState<Sector | 'todos'>('todos');
  const [soloActivas, setSoloActivas] = useState(true);
  const [mostrarCrear, setMostrarCrear] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [resultadoMotivo, setResultadoMotivo] = useState<string | null>(null);
  const [editando, setEditando] = useState<TenenciaConPersonaLote | null>(null);
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const [errorEliminar, setErrorEliminar] = useState<string | null>(null);

  async function handleEliminar(t: TenenciaConPersonaLote) {
    const ok = window.confirm(
      `¿Eliminar la tenencia de ${t.persona_nombre_completo} en casa ${t.lote_identificador}?\n\n` +
        'Esto es irreversible. Solo procede si la tenencia se cargó por error y no tiene cuotas asociadas.',
    );
    if (!ok) return;
    setEliminandoId(t.id);
    setErrorEliminar(null);
    try {
      await eliminarTenencia(t.id);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      if (err instanceof ApiError) {
        const detail =
          typeof err.body === 'object' &&
          err.body !== null &&
          'detail' in err.body
            ? String((err.body as { detail: unknown }).detail)
            : `Error ${err.status}`;
        setErrorEliminar(detail);
      } else {
        setErrorEliminar('Error de red al eliminar');
      }
    } finally {
      setEliminandoId(null);
    }
  }

  useEffect(() => {
    let cancelado = false;
    setLoading(true);
    setError(null);
    Promise.all([
      listTenencias({ activas: soloActivas }),
      listLotes({ activo: true }),
      listPersonas({ activo: true }),
    ])
      .then(([ts, ls, ps]) => {
        if (cancelado) return;
        setTenencias(ts);
        setLotes(ls);
        setPersonas(ps);
      })
      .catch((err) => {
        if (cancelado) return;
        setError(
          err instanceof ApiError
            ? `Error ${err.status} al cargar datos`
            : 'Error de red',
        );
      })
      .finally(() => {
        if (!cancelado) setLoading(false);
      });
    return () => {
      cancelado = true;
    };
  }, [refreshKey, soloActivas]);

  const filtradas = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tenencias.filter((t) => {
      if (filtroSector !== 'todos' && t.lote_sector !== filtroSector) {
        return false;
      }
      if (q) {
        const hay =
          `${t.lote_identificador} ${t.persona_nombre_completo} ${t.persona_rut}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [tenencias, search, filtroSector]);

  return (
    <div className="px-8 py-6 space-y-6">
      <PageHeader
        eyebrow="Mantenedor — Carga inicial"
        title="Tenencias"
        description="Vincula personas a casas (propietario residente, propietario no residente, arrendatario, etc.). Cerrar una tenencia (flujo ex-vecino) se hace desde el portal de la Comisión, no aquí."
        action={
          <Button variant="primary" onClick={() => setMostrarCrear(true)}>
            + Nueva tenencia
          </Button>
        }
      />

      {resultadoMotivo && (
        <div
          className="bg-state-success/15 border-l-4 border-state-success p-3 rounded-sm text-sm text-text-primary"
          role="status"
        >
          Tenencia creada. Nota: {ETIQUETA_MOTIVO[resultadoMotivo] ?? resultadoMotivo}.{' '}
          <button
            type="button"
            onClick={() => setResultadoMotivo(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 text-sm">
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por casa, persona o RUT…"
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary w-72"
        />
        <select
          value={filtroSector}
          onChange={(e) =>
            setFiltroSector(e.target.value as Sector | 'todos')
          }
          className="bg-bg-sunken border-[0.5px] border-white/15 rounded px-2 py-1.5 text-text-primary"
          aria-label="Filtrar por sector"
        >
          <option value="todos">Todos los sectores</option>
          <option value="Oriente">Oriente</option>
          <option value="Poniente">Poniente</option>
        </select>
        <label className="flex items-center gap-2 text-text-secondary">
          <input
            type="checkbox"
            checked={soloActivas}
            onChange={(e) => setSoloActivas(e.target.checked)}
          />
          <span>Solo activas</span>
        </label>
        <span className="text-xs text-text-muted">
          {filtradas.length} de {tenencias.length}
        </span>
      </div>

      <ImportCsvSection
        titulo="Carga masiva de tenencias (CSV)"
        descripcion="Para vincular varias casas y personas de una sola vez. Las personas y casas deben existir previamente."
        columnas={TENENCIAS_CSV_COLUMNAS}
        nombrePlantilla="tenencias-plantilla.csv"
        subir={importTenenciasCsv}
        onResultado={() => setRefreshKey((k) => k + 1)}
      />

      {errorEliminar && (
        <div
          className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary"
          role="alert"
        >
          {errorEliminar}{' '}
          <button
            type="button"
            onClick={() => setErrorEliminar(null)}
            className="underline ml-2"
          >
            Cerrar
          </button>
        </div>
      )}

      {loading && <p className="text-sm text-text-muted">Cargando…</p>}
      {error && !loading && (
        <div className="bg-state-danger/15 border-l-4 border-state-danger p-3 rounded-sm text-sm text-text-primary">
          {error}
        </div>
      )}
      {!loading && !error && filtradas.length === 0 && (
        <p className="text-sm text-text-muted">
          {tenencias.length === 0
            ? 'No hay tenencias registradas. Usa "+ Nueva tenencia" para vincular una casa con su responsable.'
            : 'No hay tenencias que coincidan con el filtro.'}
        </p>
      )}
      {!loading && !error && filtradas.length > 0 && (
        <Table>
          <THead>
            <Th>Casa</Th>
            <Th>Persona</Th>
            <Th>Tipo</Th>
            <Th>Desde</Th>
            <Th>Responsable pago</Th>
            <Th>Notif. morosidad</Th>
            <Th align="right">Acciones</Th>
          </THead>
          <TBody>
            {filtradas.map((t) => (
              <Tr key={t.id} data-testid="fila-tenencia">
                <Td tone="primary" className="font-medium">
                  {t.lote_identificador}{' '}
                  <span className="text-text-muted text-xs">
                    ({t.lote_sector})
                  </span>
                </Td>
                <Td>
                  {t.persona_nombre_completo}
                  <span className="text-text-muted text-xs ml-1">
                    {formatRut(t.persona_rut)}
                  </span>
                </Td>
                <Td>
                  {ETIQUETA_TIPO_TENENCIA_SA[
                    t.tipo as TipoTenenciaLiteral
                  ] ?? t.tipo}
                </Td>
                <Td>
                  {t.desde}
                  {t.hasta && (
                    <span className="text-text-muted text-xs ml-1">
                      (cerrada {t.hasta})
                    </span>
                  )}
                </Td>
                <Td>{t.responsable_pago_cuotas ? 'Sí' : 'No'}</Td>
                <Td>
                  {t.notificar_morosidad ? (
                    'Sí'
                  ) : (
                    <span className="text-state-warning">Silenciada</span>
                  )}
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="inline-flex gap-2">
                    <ActionButton
                      variant="edit"
                      onClick={() => setEditando(t)}
                      disabled={eliminandoId === t.id}
                    >
                      Editar
                    </ActionButton>
                    <ActionButton
                      variant="danger"
                      onClick={() => handleEliminar(t)}
                      disabled={eliminandoId === t.id}
                    >
                      {eliminandoId === t.id ? 'Eliminando…' : 'Eliminar'}
                    </ActionButton>
                  </div>
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}

      {mostrarCrear && (
        <ModalCrear
          lotes={lotes}
          personas={personas}
          onCerrar={() => setMostrarCrear(false)}
          onCreado={(motivo) => {
            setMostrarCrear(false);
            setResultadoMotivo(motivo);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}

      {editando && (
        <ModalEditarTenencia
          tenencia={editando}
          lotes={lotes}
          onCerrar={() => setEditando(null)}
          onActualizada={() => {
            setEditando(null);
            setRefreshKey((k) => k + 1);
          }}
        />
      )}
    </div>
  );
}
